import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { Observable, Subject, combineLatest } from 'rxjs';
import { isEqual } from 'lodash-es';

import {
  ActivateDeactivateCommands,
  CirculationPumpProperties,
  DhwHotWaterStorageTemperature,
  DhwHygieneCommands,
  DhwHygieneProperties,
  DhwTemperatureCommands,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  HeatingDhwOperatingModesActiveCommands,
  HeatingDhwOperatingModesActiveProperties,
  HeatingDhwProperties,
  HeatingDhwScheduleModeCommands,
  HeatingDhwScheduleModeProperties,
  IsActiveProperty,
  IsNumberValueProperty,
  IsStringValueProperty,
  OptimizationRecommendationsType,
} from '@vi-iot/client';

import {
  DevicesStore,
  OptimizationRecommendationSlug,
  InstallationDetailsStore,
  ViguideRecommendationsStore,
  RemoteConfigStore,
  InstallationFeaturesStore,
} from '@store';

import { AppInsightsMonitoringService } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_FEATURES } from 'src/app/shared/services/app-insights/events/features.events';
import { LogLevelEnum } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { distinctUntilChanged, filter, skip, takeUntil } from 'rxjs/operators';
import { CustomTranslationsService } from '../../../operational-data-common/translations';
import { FeaturesDetailsOverlayTypeEnum } from '../installation-overlay.model';
import { PillType } from '../../../shared/components/pill/pill.model';
import { DHW_SCHEDULE_LEVEL } from 'src/app/features/timemanager/schedule.enums';

@Component({
  selector: 'app-warm-water',
  templateUrl: './warm-water.component.html',
  styleUrls: ['./warm-water.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarmWaterComponent implements OnInit {
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly recommendationTileName = OptimizationRecommendationSlug;
  readonly pillType = PillType;
  readonly DHW_SCHEDULE_LEVEL = DHW_SCHEDULE_LEVEL;
  isDhwAvailable: boolean;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  dhw$: Observable<Feature<HeatingDhwProperties, never>>;
  dhwPumpStatus$: Observable<Feature<CirculationPumpProperties, never>>;
  dhwTemperature$: Observable<Feature<IsNumberValueProperty, DhwTemperatureCommands>>;
  storageTankSensor$: Observable<Feature<DhwHotWaterStorageTemperature, never>>;
  dhwDryContact$: Observable<Feature<IsActiveProperty, never>>;
  dhwActiveOperatingMode$: Observable<
    Feature<HeatingDhwOperatingModesActiveProperties, HeatingDhwOperatingModesActiveCommands>
  >;
  cascadeDhwMode$: Observable<Feature<IsStringValueProperty, never>>;
  dhwScheduleMode$: Observable<
    Feature<HeatingDhwScheduleModeProperties, HeatingDhwScheduleModeCommands>
  >;
  availableOverlays = FeaturesDetailsOverlayTypeEnum;
  recommendationForDhw$: Observable<OptimizationRecommendationsType[]>;
  dhwOneTimeCharge$: Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>>;
  dhwBadgeStatus: DHW_SCHEDULE_LEVEL;
  getDhwStatus$: Observable<DHW_SCHEDULE_LEVEL | undefined>;

  dhwHygiene$: Observable<Feature<DhwHygieneProperties, DhwHygieneCommands>>;

  @Input() deviceId: string;

  constructor(
    public devicesStore: DevicesStore,
    public installationDetailsStore: InstallationDetailsStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    private viguideRecommendationsStore: ViguideRecommendationsStore,
    private appInsights: AppInsightsMonitoringService,
    private remoteConfigStore: RemoteConfigStore,
    protected customTranslationsService: CustomTranslationsService
  ) {}

  ngOnInit(): void {
    this.dhw$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhw,
      this.deviceId
    );

    this.dhwPumpStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwPumpsCirculation,
      this.deviceId
    );
    this.dhwTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwMainTemperature,
      this.deviceId
    );
    this.storageTankSensor$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.storageTankSensor,
      this.deviceId
    );
    this.dhwDryContact$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwDryContact,
      this.deviceId
    );
    this.dhwActiveOperatingMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwActiveOperatingMode,
      this.deviceId
    );
    this.cascadeDhwMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.cascadeDhwMode,
      this.deviceId
    );
    this.recommendationForDhw$ = this.viguideRecommendationsStore.getAvailableRecommendationsForTile(
      [OptimizationRecommendationsType.dhwSchedule],
      OptimizationRecommendationSlug.dhw
    );
    this.dhwScheduleMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      HeatingDhwScheduleModeProperties,
      HeatingDhwScheduleModeCommands
    >(FeatureNames.dhwScheduleMode, this.deviceId);

    this.dhwHygiene$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwHygiene,
      this.deviceId
    );

    this.getDhwStatus$ = this.installationFeaturesStore.getDhwStatus$;

    combineLatest([
      this.devicesStore.isDhwAvailable$,
      this.installationFeaturesStore.selectInstallationDeviceFeature(
        FeatureNames.dhwOneTimeCharge,
        this.deviceId
      ),
      this.remoteConfigStore.isAppInsightsLoggingDataAboutDhw$.pipe(
        filter((isAppInsightsLoggingDataAboutDhw) => isAppInsightsLoggingDataAboutDhw === true)
      ),
    ])
      .pipe(distinctUntilChanged(isEqual), skip(1), takeUntil(this.ngUnsubscribe$))
      .subscribe(([isDhwOn, isOneChargeOn]) => {
        this.appInsights.logEvent(APP_INSIGHTS_FEATURES.GET_DHW_STATE_CHANGED, LogLevelEnum.TRACE, {
          dhwState: isDhwOn.isDhw,
          oneChargeState: !!isOneChargeOn?.properties?.active?.value,
          deviceId: this.deviceId,
        });
      });
  }
}
